import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Eye, Smartphone, Monitor, Plus, Trash2, 
  GripVertical, Image, Type, MapPin, Clock, Calendar, 
  FileText, HelpCircle, CheckCircle, AlertCircle, ChevronDown,
  ChevronUp, Settings, Sparkles, Upload, Link as LinkIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Section Templates
const SECTION_TEMPLATES = [
  { id: 'hero', name: 'Hero Banner', icon: Image, description: 'Eye-catching header with image/video' },
  { id: 'about', name: 'About Event', icon: FileText, description: 'Event description and details' },
  { id: 'datetime', name: 'Date & Time', icon: Calendar, description: 'When the event happens' },
  { id: 'location', name: 'Location', icon: MapPin, description: 'Venue details with map' },
  { id: 'lineup', name: 'Lineup / Schedule', icon: Clock, description: 'Event schedule or performers' },
  { id: 'goodtoknow', name: 'Good to Know', icon: AlertCircle, description: 'Age restrictions, parking, etc.' },
  { id: 'faq', name: 'FAQ', icon: HelpCircle, description: 'Frequently asked questions' },
  { id: 'gallery', name: 'Photo Gallery', icon: Image, description: 'Additional event images' },
  { id: 'sponsors', name: 'Sponsors', icon: CheckCircle, description: 'Event sponsors and partners' },
];

// Default sections for a new event
const DEFAULT_SECTIONS = [
  { 
    id: 'hero-1', 
    type: 'hero', 
    order: 0,
    data: { 
      title: '', 
      subtitle: '', 
      image_url: '', 
      video_url: '',
      overlay_opacity: 40 
    } 
  },
  { 
    id: 'about-1', 
    type: 'about', 
    order: 1,
    data: { 
      title: 'About This Event', 
      content: '' 
    } 
  },
  { 
    id: 'datetime-1', 
    type: 'datetime', 
    order: 2,
    data: {} 
  },
  { 
    id: 'location-1', 
    type: 'location', 
    order: 3,
    data: { 
      show_map: true 
    } 
  },
];

// Section Editor Components
const HeroEditor = ({ data, onChange }) => (
  <div className="space-y-4">
    <div>
      <Label>Hero Image URL</Label>
      <Input 
        placeholder="https://example.com/image.jpg"
        value={data.image_url || ''}
        onChange={(e) => onChange({ ...data, image_url: e.target.value })}
      />
      <p className="text-xs text-muted-foreground mt-1">Recommended: 1920x1080px</p>
    </div>
    <div>
      <Label>Video URL (optional)</Label>
      <Input 
        placeholder="https://youtube.com/embed/..."
        value={data.video_url || ''}
        onChange={(e) => onChange({ ...data, video_url: e.target.value })}
      />
    </div>
    <div>
      <Label>Overlay Opacity: {data.overlay_opacity || 40}%</Label>
      <input 
        type="range" 
        min="0" 
        max="80" 
        value={data.overlay_opacity || 40}
        onChange={(e) => onChange({ ...data, overlay_opacity: parseInt(e.target.value) })}
        className="w-full"
      />
    </div>
  </div>
);

const AboutEditor = ({ data, onChange }) => (
  <div className="space-y-4">
    <div>
      <Label>Section Title</Label>
      <Input 
        value={data.title || 'About This Event'}
        onChange={(e) => onChange({ ...data, title: e.target.value })}
      />
    </div>
    <div>
      <Label>Description</Label>
      <Textarea 
        rows={6}
        placeholder="Tell attendees what makes this event special..."
        value={data.content || ''}
        onChange={(e) => onChange({ ...data, content: e.target.value })}
      />
      <p className="text-xs text-muted-foreground mt-1">Supports markdown formatting</p>
    </div>
  </div>
);

const GoodToKnowEditor = ({ data, onChange }) => {
  const items = data.items || [
    { label: 'Age Requirement', value: '' },
    { label: 'Door Time', value: '' },
    { label: 'Parking', value: '' },
    { label: 'Dress Code', value: '' },
  ];

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...data, items: newItems });
  };

  const addItem = () => {
    onChange({ ...data, items: [...items, { label: '', value: '' }] });
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange({ ...data, items: newItems });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Good to Know'}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-3">
        <Label>Information Items</Label>
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <Input 
              placeholder="Label (e.g., Age)"
              value={item.label}
              onChange={(e) => updateItem(index, 'label', e.target.value)}
              className="w-1/3"
            />
            <Input 
              placeholder="Value (e.g., 21+)"
              value={item.value}
              onChange={(e) => updateItem(index, 'value', e.target.value)}
              className="flex-1"
            />
            <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addItem}>
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>
    </div>
  );
};

const FAQEditor = ({ data, onChange }) => {
  const faqs = data.faqs || [{ question: '', answer: '' }];

  const updateFaq = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    onChange({ ...data, faqs: newFaqs });
  };

  const addFaq = () => {
    onChange({ ...data, faqs: [...faqs, { question: '', answer: '' }] });
  };

  const removeFaq = (index) => {
    const newFaqs = faqs.filter((_, i) => i !== index);
    onChange({ ...data, faqs: newFaqs });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Frequently Asked Questions'}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <Card key={index} className="p-4">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-muted-foreground">Question {index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => removeFaq(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-3">
              <Input 
                placeholder="Enter question..."
                value={faq.question}
                onChange={(e) => updateFaq(index, 'question', e.target.value)}
              />
              <Textarea 
                rows={3}
                placeholder="Enter answer..."
                value={faq.answer}
                onChange={(e) => updateFaq(index, 'answer', e.target.value)}
              />
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addFaq}>
          <Plus className="w-4 h-4 mr-2" />
          Add FAQ
        </Button>
      </div>
    </div>
  );
};

const GalleryEditor = ({ data, onChange }) => {
  const images = data.images || [];

  const addImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      onChange({ ...data, images: [...images, url] });
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange({ ...data, images: newImages });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Gallery Images</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {images.map((url, index) => (
            <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button 
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button 
            onClick={addImage}
            className="aspect-square border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground hover:border-indigo-500 hover:text-indigo-500 transition-colors"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

const LineupEditor = ({ data, onChange }) => {
  const items = data.items || [];

  const addItem = () => {
    onChange({ 
      ...data, 
      items: [...items, { time: '', title: '', description: '' }] 
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...data, items: newItems });
  };

  const removeItem = (index) => {
    onChange({ ...data, items: items.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Event Schedule'}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-3">
        {items.map((item, index) => (
          <Card key={index} className="p-3">
            <div className="flex gap-2 items-start">
              <Input 
                placeholder="Time"
                value={item.time}
                onChange={(e) => updateItem(index, 'time', e.target.value)}
                className="w-24"
              />
              <div className="flex-1 space-y-2">
                <Input 
                  placeholder="Title/Performer"
                  value={item.title}
                  onChange={(e) => updateItem(index, 'title', e.target.value)}
                />
                <Input 
                  placeholder="Description (optional)"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                />
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addItem}>
          <Plus className="w-4 h-4 mr-2" />
          Add Schedule Item
        </Button>
      </div>
    </div>
  );
};

// Live Preview Component
const LivePreview = ({ event, sections, viewMode }) => {
  const getSectionContent = (section) => {
    switch (section.type) {
      case 'hero':
        return (
          <div 
            className="relative h-80 bg-cover bg-center flex items-end"
            style={{ backgroundImage: `url(${section.data.image_url || 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=1200'})` }}
          >
            <div 
              className="absolute inset-0 bg-black"
              style={{ opacity: (section.data.overlay_opacity || 40) / 100 }}
            />
            <div className="relative z-10 p-8 w-full">
              <h1 className="text-3xl font-bold text-white mb-2">{event?.title || 'Event Title'}</h1>
              <p className="text-white/80">{event?.date} • {event?.time}</p>
            </div>
          </div>
        );
      
      case 'about':
        return (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">{section.data.title || 'About This Event'}</h2>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {section.data.content || event?.description || 'Add a description for your event...'}
            </p>
          </div>
        );
      
      case 'datetime':
        return (
          <div className="p-6 bg-indigo-50 dark:bg-indigo-950">
            <h2 className="text-xl font-bold mb-4">Date & Time</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <span>{event?.date || 'TBD'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" />
                <span>{event?.time || 'TBD'}</span>
              </div>
            </div>
          </div>
        );
      
      case 'location':
        return (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Location</h2>
            <div className="flex items-start gap-2 mb-4">
              <MapPin className="w-5 h-5 text-indigo-600 mt-1" />
              <div>
                <p className="font-medium">{event?.venue_name || 'Venue Name'}</p>
                <p className="text-muted-foreground text-sm">123 Main St, Denver, CO</p>
              </div>
            </div>
            {section.data.show_map && (
              <div className="h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-muted-foreground">Map Preview</span>
              </div>
            )}
          </div>
        );
      
      case 'goodtoknow':
        const items = section.data.items || [];
        return (
          <div className="p-6 bg-yellow-50 dark:bg-yellow-950">
            <h2 className="text-xl font-bold mb-4">{section.data.title || 'Good to Know'}</h2>
            <div className="grid grid-cols-2 gap-4">
              {items.filter(i => i.label && i.value).map((item, idx) => (
                <div key={idx}>
                  <p className="text-sm text-muted-foreground">{item.label}</p>
                  <p className="font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'faq':
        const faqs = section.data.faqs || [];
        return (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">{section.data.title || 'FAQ'}</h2>
            <div className="space-y-3">
              {faqs.filter(f => f.question).map((faq, idx) => (
                <div key={idx} className="border-b pb-3">
                  <p className="font-medium">{faq.question}</p>
                  <p className="text-muted-foreground text-sm mt-1">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'lineup':
        const scheduleItems = section.data.items || [];
        return (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">{section.data.title || 'Schedule'}</h2>
            <div className="space-y-3">
              {scheduleItems.map((item, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <span className="text-indigo-600 font-mono text-sm w-16">{item.time}</span>
                  <div>
                    <p className="font-medium">{item.title}</p>
                    {item.description && (
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'gallery':
        const images = section.data.images || [];
        return (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Gallery</h2>
            <div className="grid grid-cols-3 gap-2">
              {images.map((url, idx) => (
                <div key={idx} className="aspect-square rounded-lg overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div 
      className={cn(
        "bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden transition-all",
        viewMode === 'mobile' ? 'w-[375px]' : 'w-full'
      )}
    >
      {sections
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <div key={section.id}>
            {getSectionContent(section)}
          </div>
        ))}
      
      {/* CTA Section */}
      <div className="p-6 bg-gray-50 dark:bg-gray-800 border-t">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold">${event?.price || '0'}</p>
            <p className="text-sm text-muted-foreground">per ticket</p>
          </div>
          <Button className="bg-indigo-600 hover:bg-indigo-700">
            Get Tickets
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const EventPageBuilder = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { isAuthenticated, isPartner, user, loading: authLoading } = useAuth();
  const [event, setEvent] = useState(null);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [selectedSection, setSelectedSection] = useState(null);
  const [viewMode, setViewMode] = useState('desktop');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seoSettings, setSeoSettings] = useState({
    custom_url: '',
    meta_title: '',
    meta_description: '',
    og_image: ''
  });

  useEffect(() => {
    if (authLoading) return;
    
    // Allow both partners and admins
    if (!isAuthenticated || (!isPartner && user?.role !== 'admin')) {
      navigate('/auth');
      return;
    }
    
    fetchEvent();
  }, [isAuthenticated, isPartner, authLoading, eventId, user]);

  const fetchEvent = async () => {
    try {
      const response = await api.get(`/events/${eventId}`);
      setEvent(response.data);
      
      // Load saved sections if available
      if (response.data.page_sections) {
        setSections(response.data.page_sections);
      } else {
        // Initialize with default sections using event data
        setSections([
          { 
            id: 'hero-1', 
            type: 'hero', 
            order: 0,
            data: { 
              title: response.data.title, 
              image_url: response.data.images?.[0] || '', 
              overlay_opacity: 40 
            } 
          },
          { 
            id: 'about-1', 
            type: 'about', 
            order: 1,
            data: { 
              title: 'About This Event', 
              content: response.data.description 
            } 
          },
          { id: 'datetime-1', type: 'datetime', order: 2, data: {} },
          { id: 'location-1', type: 'location', order: 3, data: { show_map: true } },
        ]);
      }
      
      // Load SEO settings
      setSeoSettings({
        custom_url: response.data.custom_url || '',
        meta_title: response.data.meta_title || response.data.title,
        meta_description: response.data.meta_description || response.data.description?.substring(0, 160),
        og_image: response.data.og_image || ''
      });
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error('Failed to load event');
      navigate('/partner/events');
    } finally {
      setLoading(false);
    }
  };

  const addSection = (template) => {
    const newSection = {
      id: `${template.id}-${Date.now()}`,
      type: template.id,
      order: sections.length,
      data: {}
    };
    setSections([...sections, newSection]);
    setSelectedSection(newSection.id);
  };

  const removeSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
    if (selectedSection === sectionId) {
      setSelectedSection(null);
    }
  };

  const updateSectionData = (sectionId, data) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, data } : s
    ));
  };

  const moveSection = (sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === sections.length - 1)
    ) return;

    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    // Update order values
    newSections.forEach((s, i) => s.order = i);
    setSections(newSections);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/page`, {
        page_sections: sections,
        custom_url: seoSettings.custom_url,
        meta_title: seoSettings.meta_title,
        meta_description: seoSettings.meta_description,
        og_image: seoSettings.og_image
      });
      toast.success('Event page saved!');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getSectionEditor = (section) => {
    const props = {
      data: section.data,
      onChange: (data) => updateSectionData(section.id, data)
    };

    switch (section.type) {
      case 'hero': return <HeroEditor {...props} />;
      case 'about': return <AboutEditor {...props} />;
      case 'goodtoknow': return <GoodToKnowEditor {...props} />;
      case 'faq': return <FAQEditor {...props} />;
      case 'gallery': return <GalleryEditor {...props} />;
      case 'lineup': return <LineupEditor {...props} />;
      case 'datetime':
      case 'location':
        return (
          <div className="text-center py-8 text-muted-foreground">
            <p>This section automatically uses your event data.</p>
            <p className="text-sm mt-2">Edit event details to update this section.</p>
          </div>
        );
      default:
        return <p className="text-muted-foreground">No editor available for this section type.</p>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      {/* Header */}
      <header className="h-16 bg-white dark:bg-gray-900 border-b fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/partner/events/${eventId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
          <h1 className="font-bold text-foreground">Event Page Builder</h1>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <Button 
              variant={viewMode === 'desktop' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('desktop')}
            >
              <Monitor className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === 'mobile' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('mobile')}
            >
              <Smartphone className="w-4 h-4" />
            </Button>
          </div>

          {/* SEO Settings */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                SEO
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>SEO Settings</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-6">
                <div>
                  <Label>Custom URL</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-sm text-muted-foreground bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-l-md border border-r-0">
                      eventgoers.com/e/
                    </span>
                    <Input 
                      className="rounded-l-none"
                      placeholder="your-event-name"
                      value={seoSettings.custom_url}
                      onChange={(e) => setSeoSettings({ ...seoSettings, custom_url: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use lowercase letters, numbers, and hyphens only
                  </p>
                </div>
                <div>
                  <Label>Meta Title</Label>
                  <Input 
                    value={seoSettings.meta_title}
                    onChange={(e) => setSeoSettings({ ...seoSettings, meta_title: e.target.value })}
                    placeholder="Event title for search engines"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {seoSettings.meta_title?.length || 0}/60 characters
                  </p>
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <Textarea 
                    rows={3}
                    value={seoSettings.meta_description}
                    onChange={(e) => setSeoSettings({ ...seoSettings, meta_description: e.target.value })}
                    placeholder="Brief description for search results"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {seoSettings.meta_description?.length || 0}/160 characters
                  </p>
                </div>
                <div>
                  <Label>Social Share Image</Label>
                  <Input 
                    placeholder="https://example.com/og-image.jpg"
                    value={seoSettings.og_image}
                    onChange={(e) => setSeoSettings({ ...seoSettings, og_image: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 1200x630px
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Button variant="outline" onClick={() => window.open(`/events/${eventId}`, '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={saveChanges}
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-16 flex">
        {/* Left Panel - Section List */}
        <aside className="w-72 bg-white dark:bg-gray-900 border-r fixed left-0 top-16 bottom-0 overflow-y-auto">
          <div className="p-4 border-b">
            <h2 className="font-bold text-foreground">Page Sections</h2>
            <p className="text-sm text-muted-foreground">Drag to reorder</p>
          </div>
          
          <div className="p-2 space-y-1">
            {sections
              .sort((a, b) => a.order - b.order)
              .map((section, index) => {
                const template = SECTION_TEMPLATES.find(t => t.id === section.type);
                const Icon = template?.icon || FileText;
                
                return (
                  <div 
                    key={section.id}
                    className={cn(
                      "p-3 rounded-lg cursor-pointer flex items-center gap-3 group transition-colors",
                      selectedSection === section.id 
                        ? "bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800" 
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSelectedSection(section.id)}
                  >
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                    <Icon className="w-4 h-4 text-gray-500" />
                    <span className="flex-1 text-sm font-medium">{template?.name || section.type}</span>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'up'); }}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'down'); }}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Add Section */}
          <div className="p-4 border-t">
            <p className="text-sm font-medium mb-3">Add Section</p>
            <div className="grid grid-cols-2 gap-2">
              {SECTION_TEMPLATES.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => addSection(template)}
                    className="p-3 border rounded-lg text-left hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950 transition-colors"
                  >
                    <Icon className="w-4 h-4 text-indigo-600 mb-1" />
                    <p className="text-xs font-medium">{template.name}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Center - Preview */}
        <main className="flex-1 ml-72 mr-80 p-6 flex justify-center">
          <LivePreview 
            event={event} 
            sections={sections} 
            viewMode={viewMode}
          />
        </main>

        {/* Right Panel - Section Editor */}
        <aside className="w-80 bg-white dark:bg-gray-900 border-l fixed right-0 top-16 bottom-0 overflow-y-auto">
          {selectedSection ? (
            <>
              <div className="p-4 border-b">
                <h2 className="font-bold text-foreground">Edit Section</h2>
                <p className="text-sm text-muted-foreground">
                  {SECTION_TEMPLATES.find(t => t.id === sections.find(s => s.id === selectedSection)?.type)?.name}
                </p>
              </div>
              <div className="p-4">
                {getSectionEditor(sections.find(s => s.id === selectedSection))}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a section to edit</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default EventPageBuilder;
